import { zodResolver } from '@hookform/resolvers/zod'
import { FC, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { validateCPF, validateDate } from 'src/utils/validations'
import { z } from 'zod'
import { FormInput } from './components'

import appStore from 'src/assets/product-config/app-store.png'
import background from 'src/assets/product-config/background-signin.png'
import android from 'src/assets/product-config/google-play.png'

import { ArrowLeft } from 'lucide-react'
import './styles.css'

const FormSchema = z.object({
  name: z.string({
    required_error: 'Insira o nome do Sócio.',
  }),
  bornDate: z.string({
    required_error: 'Insira a Data de nascimento.',
  }),
  cpf: z.string({
    required_error: 'Insira o CPF.',
  }),
  motherName: z.string({
    required_error: 'Insira o Nome da Mãe.',
  }),
})

type FormInputs = z.infer<typeof FormSchema>

export const InsertPartnerData: FC = () => {
  const location = useLocation()

  const { accountType } = location.state

  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [isPartnerPEP, setIsPartnerPEP] = useState<boolean | undefined>(
    undefined
  )

  const { control, handleSubmit, setError } = useForm<FormInputs>({
    resolver: zodResolver(FormSchema),
  })

  async function handlePartner(data: FormInputs) {
    if (!data.name?.includes(' ') || data.name?.length < 5) {
      setError('name', { message: 'Preencha o nome completo.' })
      return
    }
    if (!data.motherName?.includes(' ') || data.motherName?.length < 5) {
      setError('motherName', { message: 'Preencha o nome da mãe completo.' })

      return
    }

    if (!validateCPF(data.cpf.replace(/[/,.,-\s]/g, ''))) {
      setError('cpf', { message: 'CPF Inválido.' })
      return
    }

    if (!validateDate(data.bornDate)) {
      setError('bornDate', { message: 'Data de Nascimento inválida.' })
      return
    }

    setLoading(true)
    try {
      localStorage.setItem('@yntech:signup-data-partner', JSON.stringify(data))
      navigate('/create-account-wallet/insert-partner-account-address', {
        state: { accountType },
      })
      setLoading(false)
    } catch (err) {
      setLoading(false)
    }
  }

  return (
    <div
      style={{
        backgroundImage: `url(${background})
`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center',
      }}
      className="w-full flex-col h-screen flex justify-center items-center gap-20"
    >
      <button
        onClick={() => navigate('/create-account-wallet/choose-account-type')}
        className="h-12 w-12 absolute left-10 top-10 flex items-center justify-center rounded-xl bg-zinc-950 shadow-sm"
      >
        <ArrowLeft size={22} color="white" />
      </button>
      <div className="flex flex-col absolute bottom-10 left-10">
        <span className="text-white text-xl font-bold">Baixe o nosso app,</span>
        <span className="text-white text-xl">
          disponível nas principais lojas
        </span>
        <div className="flex gap-2 mt-2">
          <img
            alt=""
            src={android}
            className="w-[150px] object-contain cursor-pointer"
          />
          <img
            alt=""
            src={appStore}
            className="w-[150px] object-contain cursor-pointer"
          />
        </div>
      </div>
      <div className="bg-black mr-10 self-end bg-opacity-40 rounded-3xl backdrop-filter backdrop-blur-md border border-gray-600 p-12 max-w-[50%] flex flex-col justify-center items-center">
        <div className="w-full">
          <div className="w-full flex items-start gap-10">
            <p className="font-medium text-white text-3xl full-hd:text-4xl 3xl-hd:text-5xl qhd:text-5xl studio-laptop:text-6xl qhd-plus:text-6xl 4k:text-8xl 5k:text-8xl leading-7 mb-[4%]">
              Você está a poucos passos de uma <br />
              nova <strong>experiência financeira</strong>
            </p>
          </div>

          <p className="text-white p-texte font-light mt-3 mb-4">
            Informe os dados de um sócio majoritário ou <br />
            administrador
          </p>
          <form onSubmit={handleSubmit(handlePartner)}>
            {fields.map((item: any, index: number) => {
              return (
                <div key={item.id}>
                  {item.breakPoint === false && (
                    <Controller
                      name={item.id as any}
                      control={control}
                      render={({ field, fieldState }) => (
                        <FormInput
                          placeholder={item.placeholder}
                          required
                          value={field.value}
                          onChangeText={(e) => field.onChange(e)}
                          mask={item.mask}
                          errorMessage={fieldState.error?.message}
                        />
                      )}
                    />
                  )}

                  {item.breakPoint && (
                    <div className="flex items-center gap-5 justify-between">
                      <Controller
                        name={item.id as any}
                        control={control}
                        render={({ field, fieldState }) => (
                          <FormInput
                            style={{
                              width: '20vw',
                            }}
                            onChangeText={(e) => field.onChange(e)}
                            placeholder={item.placeholder}
                            required
                            {...field}
                            mask={item.mask}
                            errorMessage={fieldState.error?.message}
                          />
                        )}
                      />
                      <Controller
                        name={fields[index + 1].id as any}
                        control={control}
                        render={({ field, fieldState }) => (
                          <FormInput
                            style={{
                              width: '20vw',
                            }}
                            placeholder={fields[index + 1].placeholder}
                            required
                            onChangeText={(e) => field.onChange(e)}
                            {...field}
                            mask={fields[index + 1].mask}
                            errorMessage={fieldState.error?.message}
                          />
                        )}
                      />
                    </div>
                  )}
                </div>
              )
            })}

            <p className="text-white text-base font-extralight mt-2 mb-3">
              O sócio é PEP?
            </p>

            <div className="flex items-center gap-5">
              <button
                type="button"
                onClick={() => setIsPartnerPEP(true)}
                className="flex items-center justify-center gap-2 text-white font-extralight"
              >
                <div className="w-4 h-4 rounded-full border-[1px] border-white flex items-center justify-center">
                  {isPartnerPEP && (
                    <div className="rounded-full bg-white w-2 h-2"></div>
                  )}
                </div>
                Sim
              </button>
              <button
                type="button"
                onClick={() => setIsPartnerPEP(false)}
                className="flex items-center justify-center gap-2 text-white font-extralight"
              >
                <div className="w-4 h-4 rounded-full border-[1px] border-white flex items-center justify-center">
                  {!isPartnerPEP && (
                    <div className="rounded-full bg-white w-2 h-2"></div>
                  )}
                </div>
                Não
              </button>
            </div>
            <div className="flex flex-col w-full gap-2 items-center justify-between mt-4">
              <button
                className="bg-white h-16 w-full border border-zinc-300 rounded-md font-bold"
                onClick={handleSubmit(handlePartner)}
              >
                Próximo
              </button>
              <button
                className="bg-zinc-900 h-16 w-full border border-zinc-300 rounded-md font-bold text-white"
                onClick={() =>
                  navigate('/create-account-wallet/insert-account-address', {
                    state: { accountType },
                  })
                }
              >
                Voltar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

const fields = [
  {
    id: 'name',
    placeholder: 'Nome do sócio',
    breakPoint: false,
  },
  {
    id: 'bornDate',
    placeholder: 'Data de nascimento',
    mask: '99/99/9999',
    breakPoint: true,
  },
  {
    id: 'cpf',
    placeholder: 'CPF',
    breakPoint: undefined,
    mask: '999-999-999-99',
  },
  {
    id: 'motherName',
    placeholder: 'Nome da mãe',
    breakPoint: false,
  },
]
